import React, { useCallback } from 'react';
import type { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { LessonFormValues } from '../schemas/lessonSchema';
import type { Assessment } from '../types';

interface ContentBasedFieldsProps {
  contentType: string | undefined;
  assessments: Assessment[];
  register: UseFormRegister<LessonFormValues>;
  setValue: UseFormSetValue<LessonFormValues>;
  watch: UseFormWatch<LessonFormValues>;
  onContentTypeChange: (type: string) => void;
  uploadedFile: File | null;
  onFileChange: (file: File | null) => void;
}

const CONTENT_TYPE_OPTIONS = [
  { value: 'document', label: 'Document (PDF/DOC)' },
  { value: 'mp4', label: 'Video (MP4)' },
  { value: 'scorm', label: 'SCORM Package' },
  { value: 'url', label: 'External URL' },
  { value: 'quiz', label: 'Quiz / Assessment' },
] as const;

const FILE_ACCEPT_MAP: Record<string, string> = {
  mp4: 'video/*',
  document: '.pdf,.doc,.docx',
  scorm: '.zip',
};

const FILE_HINT_MAP: Record<string, string> = {
  mp4: 'MP4, MOV, AVI up to 500MB',
  document: 'PDF, DOC, DOCX up to 50MB',
  scorm: 'ZIP file containing SCORM package',
};

const FILE_LABEL_MAP: Record<string, string> = {
  mp4: 'Video',
  document: 'Document',
  scorm: 'SCORM Package',
};

function getFileFieldName(contentType: string): keyof LessonFormValues {
  switch (contentType) {
    case 'mp4': return 'videoUpload';
    case 'document': return 'lessonContentDocument';
    default: return 'scormPackage';
  }
}

const ContentBasedFields: React.FC<ContentBasedFieldsProps> = ({
  contentType,
  assessments,
  register,
  setValue,
  watch,
  onContentTypeChange,
  uploadedFile,
  onFileChange,
}) => {
  const selectStyle = {
    padding: '10px',
    fontSize: '14px',
    fontWeight: '500' as const,
    cursor: 'pointer',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '6px',
    width: '100%',
  };

  const handleFileSelected = useCallback((file: File) => {
    onFileChange(file);
    const fieldName = getFileFieldName(contentType ?? 'document');
    setValue(fieldName, file.name);
  }, [contentType, onFileChange, setValue]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelected(file);
  }, [handleFileSelected]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
  }, [handleFileSelected]);

  const currentFileField = contentType
    ? watch(getFileFieldName(contentType))
    : undefined;

  const displayFileName = currentFileField
    ? (typeof currentFileField === 'string' ? currentFileField : (currentFileField as File).name)
    : null;

  return (
    <>
      {/* Content Type Selector */}
      <div className="form-row">
        <div className="form-group">
          <label>Content Type *</label>
          <select
            value={contentType ?? 'document'}
            onChange={(e) => onContentTypeChange(e.target.value)}
            className="form-select"
            style={selectStyle}
          >
            {CONTENT_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Quiz / Assessment */}
      {contentType === 'quiz' && (
        <QuizFields
          assessments={assessments}
          register={register}
          setValue={setValue}
          watch={watch}
          selectStyle={selectStyle}
        />
      )}

      {/* URL Input */}
      {contentType === 'url' && (
        <div className="form-row">
          <div className="form-group">
            <label>Content URL</label>
            <input
              type="url"
              {...register('contentUrl')}
              placeholder="https://example.com/content"
              className="form-input"
            />
          </div>
        </div>
      )}

      {/* File Upload */}
      {contentType && !['quiz', 'url'].includes(contentType) && (
        <div className="form-row">
          <div className="form-group">
            <label>Upload {FILE_LABEL_MAP[contentType] ?? 'File'}</label>
            <div
              style={{
                border: '2px dashed #ccc',
                borderRadius: '8px',
                padding: '30px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: '#fafafa',
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="content-upload"
                onChange={handleInputChange}
                accept={FILE_ACCEPT_MAP[contentType] ?? '*'}
                style={{ display: 'none' }}
              />
              <label htmlFor="content-upload" style={{ cursor: 'pointer', width: '100%' }}>
                <div style={{ color: '#666', marginBottom: '10px', fontSize: '14px' }}>
                  {displayFileName ? (
                    <div style={{ color: '#4CAF50', fontWeight: '500' }}>
                      {displayFileName}
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                        Click to upload or drag and drop
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {FILE_HINT_MAP[contentType] ?? ''}
                      </div>
                    </>
                  )}
                </div>
              </label>
            </div>
            <p style={{ fontSize: '12px', color: '#999', marginTop: '10px', marginBottom: 0 }}>
              Note: File will be uploaded after the lesson is saved
            </p>
          </div>
        </div>
      )}
    </>
  );
};

// --- Quiz sub-section ---

interface QuizFieldsProps {
  assessments: Assessment[];
  register: UseFormRegister<LessonFormValues>;
  setValue: UseFormSetValue<LessonFormValues>;
  watch: UseFormWatch<LessonFormValues>;
  selectStyle: React.CSSProperties;
}

const QuizFields: React.FC<QuizFieldsProps> = ({
  assessments,
  register,
  setValue,
  watch,
  selectStyle,
}) => {
  const requireSectionCompletion = watch('requireSectionCompletion');
  const assessmentStartDate = watch('assessmentStartDate');
  const assessmentEndDate = watch('assessmentEndDate');
  const hasDateRange = !!(assessmentStartDate || assessmentEndDate);

  return (
    <>
      <div className="form-row">
        <div className="form-group">
          <label>Select Assessment / Quiz *</label>
          <select
            {...register('assessmentId', { valueAsNumber: true })}
            className="form-select"
            style={selectStyle}
          >
            <option value="">-- Select an Assessment --</option>
            {assessments.map(assessment => (
              <option key={assessment.id} value={assessment.id}>
                {assessment.name} ({assessment.type} - {assessment.questions} questions, {assessment.time})
              </option>
            ))}
          </select>
          {assessments.length === 0 && (
            <p style={{ fontSize: '12px', color: '#999', marginTop: '5px', marginBottom: 0 }}>
              No active assessments available. Please create assessments in the Assessment Management section first.
            </p>
          )}
        </div>
      </div>

      {/* Access Control Settings */}
      <div style={{
        backgroundColor: '#f0f7ff',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #bbdefb',
        marginTop: '10px',
      }}>
        <h4 style={{ margin: '0 0 15px 0', fontSize: '14px', fontWeight: '600', color: '#1565c0' }}>
          Access Control Settings
        </h4>

        {/* Require Section Completion */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#333',
          }}>
            <input
              type="checkbox"
              checked={requireSectionCompletion ?? false}
              onChange={(e) => setValue('requireSectionCompletion', e.target.checked)}
              style={{ marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <div>
              <strong>Require all lessons in section to be completed</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                Users must complete all other lessons in this section before they can access this quiz/assessment.
              </p>
            </div>
          </label>
        </div>

        {/* Date Range */}
        <div>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#333',
            marginBottom: '10px',
          }}>
            <input
              type="checkbox"
              checked={hasDateRange}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue('assessmentStartDate', '');
                  setValue('assessmentEndDate', '');
                }
              }}
              style={{ marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <div>
              <strong>Restrict to specific date range</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                Users can only take this quiz/assessment within the specified start and end dates.
              </p>
            </div>
          </label>

          <div className="form-row" style={{ marginLeft: '28px', marginTop: '10px' }}>
            <div className="form-group">
              <label style={{ fontSize: '13px' }}>Start Date</label>
              <input type="date" {...register('assessmentStartDate')} className="form-input" />
            </div>
            <div className="form-group">
              <label style={{ fontSize: '13px' }}>End Date</label>
              <input type="date" {...register('assessmentEndDate')} className="form-input" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContentBasedFields;
